// Client-side MP3/MP4 ID3 tag reader using jsmediatags.
// Used to autofill submission and admin upload forms when the audio file has embedded metadata.
//
// Returns a normalized object: { title, artist, album, genre, year, lyrics, coverDataUrl }
// or null if parsing fails / file is missing tags.
//
// Notes:
// - Pure client-side. No network. No backend involvement.
// - Catches all errors silently — caller decides UX.
// - Suno MP3s are well-supported (USLT, APIC, TIT2, TPE1 frames).

// Use the prebuilt browser bundle (UMD); the package's "main" entry pulls Node-only fs/buffer modules.
import jsmediatags from "jsmediatags/dist/jsmediatags.min.js";

function bytesToBase64(bytes) {
  // bytes is an Array of integers (0-255) per jsmediatags spec
  let bin = "";
  const len = bytes.length;
  for (let i = 0; i < len; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function readTags(file) {
  return new Promise((resolve) => {
    try {
      jsmediatags.read(file, {
        onSuccess: (result) => resolve(result),
        onError: () => resolve(null),
      });
    } catch (_e) {
      resolve(null);
    }
  });
}

export async function readAudioMetadata(file) {
  if (!file) return null;
  // Limit to plausibly-tagged audio files (jsmediatags supports MP3/MP4/M4A/AAC/FLAC).
  // Skip outright video to avoid wasting work on big MP4s.
  const name = (file.name || "").toLowerCase();
  if (name.endsWith(".webm") || name.endsWith(".wav") || name.endsWith(".ogg")) {
    return null;
  }
  const result = await readTags(file);
  if (!result || !result.tags) return null;
  const t = result.tags;

  const title = (t.title || "").trim();
  const artist = (t.artist || "").trim();
  const album = (t.album || "").trim();
  const genre = (t.genre || "").trim();
  const year = (t.year || "").toString().trim();
  // Lyrics live in either USLT (unsynced) or SYLT frames in ID3v2.
  let lyrics = "";
  if (t.lyrics && typeof t.lyrics === "string") {
    lyrics = t.lyrics;
  } else if (t.USLT && t.USLT.data && t.USLT.data.lyrics) {
    lyrics = t.USLT.data.lyrics;
  } else if (t.unsynchronisedLyrics && t.unsynchronisedLyrics.lyrics) {
    lyrics = t.unsynchronisedLyrics.lyrics;
  }
  lyrics = (lyrics || "").trim();

  // Cover art: APIC frame for ID3v2, or "picture" for MP4
  let coverDataUrl = null;
  let coverMime = null;
  if (t.picture && t.picture.data && t.picture.data.length) {
    coverMime = t.picture.format || "image/jpeg";
    coverDataUrl = `data:${coverMime};base64,${bytesToBase64(t.picture.data)}`;
  }

  // Return null if there is literally nothing useful — saves UI noise.
  if (!title && !artist && !album && !genre && !year && !lyrics && !coverDataUrl) {
    return null;
  }

  return { title, artist, album, genre, year, lyrics, coverDataUrl, coverMime };
}

// Convert a base64 data URL into a File so it can be appended to a FormData.
export function coverDataUrlToFile(dataUrl, suggestedName = "embedded-cover.jpg") {
  if (!dataUrl) return null;
  const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!match) return null;
  const mime = match[1];
  const b64 = match[2];
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  // Pick a sane extension based on mime
  let ext = "jpg";
  if (mime.includes("png")) ext = "png";
  else if (mime.includes("webp")) ext = "webp";
  const name = suggestedName.replace(/\.[a-z]+$/i, "") + "." + ext;
  return new File([arr], name, { type: mime });
}
